<template>
  <div class="messages__chat" role="region" aria-labelledby="chat_header">
    <ChatHeader
      ref="chatHeader"
      :partner="chat.partner"
      :show-partner-details="showPartnerDetails"
      @v-show-partner-details-toggle="togglePartnerDetails"
    />
    <div
      class="messages__chat__overlay_wrapper"
      :class="[showPartnerDetails ? 'messages__chat__overlay_wrapper--dimmed' : '']"
    >
      <ChatForm :scope="scope" :partner-id="partnerId" :partner-name="partnerName" />
      <div
        ref="messages"
        class="messages__chat__messages_wrapper"
        :tabindex="Object.keys(groupedMessages).length > 0 ? 0 : -1"
        @v-messages-scroll-top-reached="fetchMoreMessages"
      >
        <div :aria-label="messagesLabel" role="feed" :aria-busy="String(loadingMore)" class="messages__chat__messages">
          <template v-for="{ day: day, messages: messages } in groupedMessages" :key="day">
            <div class="messages__chat__day sr_only">
              {{ formatDayForChat(day, { speakable: true }) }}
            </div>
            <ChatMessage
              v-for="message in messages"
              :key="message.id"
              :message="message"
              :partner-name="partnerName"
              :on-focus="onMessageFocus(message.isLastSoFar)"
            />
            <div class="messages__chat__day test-chat_day" aria-hidden="true">
              {{ formatDayForChat(day) }}
            </div>
          </template>
          <Loader class="messages__chat__loader" :loading="loadingMore" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ChatHeader from './ChatHeader.vue'
import ChatMessage from './ChatMessage.vue'
import ChatForm from './ChatForm.vue'
import Loader from './Loader.vue'
import { mapGetters, mapActions } from 'vuex'
import { groupBy, toStartOfDayTimestamp, formatDayForChat } from './utils'

export default {
  name: 'Chat',
  components: { ChatHeader, ChatMessage, ChatForm, Loader },
  data: function () {
    return {
      loadingMore: false,
      showPartnerDetails: false,
      scrollToBottom: false,
      fetchedPagesCount: 0
    }
  },
  computed: {
    ...mapGetters(['getChat', 'getChatPartnerId']),
    groupedMessages() {
      const groupedAsc = groupBy(this.chat.messages, message => toStartOfDayTimestamp(message.inserted_at))

      const keys = Object.keys(groupedAsc)

      // everything gets reversed for a better screen reader experience
      return keys.reverse().reduce(function (acc, key, index) {
        const messages = groupedAsc[key].reverse()

        if (index === keys.length - 1) {
          messages[messages.length - 1].isLastSoFar = true
        }

        return [...acc, { day: key, messages }]
      }, [])
    },
    chat() {
      return this.getChat(this.scope, this.partnerId)
    },
    scope() {
      return this.$route.params.chatScope
    },
    partnerId() {
      return this.getChatPartnerId(this.scope, this.$route.params.chat)
    },
    shouldRequestMore() {
      return (
        !this.chat.fullyLoaded &&
        (!this.chat.fetchedPagesCount || this.chat.fetchedPagesCount <= this.fetchedPagesCount)
      )
    },
    partnerName() {
      if (this.chat.partner.member) {
        return this.chat.partner.member.first_name
      } else {
        return this.chat.partner.publication.title
      }
    },
    messagesLabel() {
      return this.i18n.$gettext('Messages')
    }
  },
  async mounted() {
    // Only request messages if the chat is not already fully loaded and
    // the store doesn't already have more pages of messages
    if (this.shouldRequestMore) {
      this.fetchedPagesCount = this.fetchedPagesCount + 1
      await this.fetchChatMessages({
        scope: this.scope,
        partnerId: this.partnerId,
        fetchedPagesCount: this.fetchedPagesCount
      })
    } else {
      this.fetchedPagesCount = this.chat.fetchedPagesCount
    }

    const messages = this.$refs.messages

    messages.addEventListener('scroll', this.onScrollTopReached)
    document.addEventListener('visibilitychange', this.handleWindowFocusEvent)

    this.$nextTick(function () {
      // Set the scrollbar to the bottom of the chat when the DOM is first updated
      const messages = this.$refs.messages
      messages.scrollTop = messages.scrollHeight
    })

    this.markAsRead()
  },
  beforeUpdate() {
    const messages = this.$refs.messages
    const scrolled = messages.scrollTop + messages.clientHeight + 1 < messages.scrollHeight

    this.scrollToBottom = !scrolled
  },
  updated() {
    if (this.scrollToBottom) {
      this.$refs.messages.scrollTop = this.$refs.messages.scrollHeight
    }
  },
  beforeUnmount: function () {
    this.$refs.messages.removeEventListener('scroll', this.onScrollTopReached)
    document.removeEventListener('visibilitychange', this.handleWindowFocusEvent)
  },
  methods: {
    ...mapActions(['fetchChatMessages', 'markMessagesAsRead']),
    async fetchMoreMessages() {
      if (!this.shouldRequestMore) {
        return
      }

      this.fetchedPagesCount = this.fetchedPagesCount + 1
      this.loadingMore = true

      const oldScrollHeight = this.$refs.messages.scrollHeight

      await this.fetchChatMessages({
        scope: this.scope,
        partnerId: this.partnerId,
        fetchedPagesCount: this.fetchedPagesCount
      })

      this.$nextTick(function () {
        const messages = this.$refs.messages
        const newScrollHeight = messages.scrollHeight
        messages.scrollTop = newScrollHeight - oldScrollHeight
      })

      this.markAsRead()
      this.loadingMore = false
    },
    onScrollTopReached() {
      const messages = this.$refs.messages
      const scrolled = messages.scrollTop + messages.clientHeight + 1 < messages.scrollHeight

      if (scrolled && messages.scrollTop === 0) {
        messages.dispatchEvent(new Event('v-messages-scroll-top-reached'))
      }
    },
    togglePartnerDetails() {
      if (this.showPartnerDetails) {
        this.hidePartnerDetails()
      } else {
        this.showPartnerDetails = true
        setTimeout(() => {
          document.body.addEventListener('click', this.hidePartnerDetails)
        }, 0)
      }
    },
    hidePartnerDetails(event) {
      if (!event || !event.target.closest('.js-partner_details')) {
        this.showPartnerDetails = false
        document.body.removeEventListener('click', this.hidePartnerDetails)
      }
    },
    markAsRead() {
      if (!document.visibilityState === 'visible') {
        return
      }

      const messageIds = this.chat.messages
        .filter(msg => {
          return !msg.read_at && msg.type === 'received'
        })
        .map(msg => msg.id)

      if (messageIds.length > 0) {
        this.markMessagesAsRead({
          scope: this.scope,
          partnerId: this.partnerId,
          messageIds
        })
      }
    },
    handleWindowFocusEvent() {
      setTimeout(this.markAsRead, 2_000)
    },
    onMessageFocus(isLastSoFar) {
      // It is necessary to fetch more messages while the focus is on the last message (or second last, or so),
      // not after it, so that the screen reader can go forward to the new, just-inserted message.
      // If we would only fetch new messages after all messages were read,
      // the reading context of the screen reader would go to the end of the container and miss the new messages.
      // See https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/Feed_Role
      if (isLastSoFar) {
        return () => this.fetchMoreMessages()
      } else {
        return () => {}
      }
    },
    formatDayForChat(day, opts = {}) {
      return formatDayForChat(day, opts)
    }
  }
}
</script>
